import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { itineraryAPI } from '../services/api';
import './ItineraryDashboard.css';

const ItineraryDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const data = await itineraryAPI.getStats();
      // API returns { success, data: { ... } } — unwrap the inner data
      setStats(data.data || data);
    } catch (err) {
      setError('Failed to load dashboard stats.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Layout title="Itinerary Dashboard"><div className="itin-loading">Loading dashboard...</div></Layout>;
  if (error) return <Layout title="Itinerary Dashboard"><div className="itin-error">{error}</div></Layout>;

  const s = stats || {};
  // Convert breakdown_by_interest object → sorted array for chart
  const interests = Object.entries(s.breakdown_by_interest || {})
    .map(([interest, count]) => ({ interest, count }))
    .sort((a, b) => b.count - a.count);
  const districts = s.top_districts || [];
  const topAttractions = s.top_attractions || [];
  const maxInterest = interests.length ? Math.max(...interests.map(i => i.count)) : 1;
  const maxDistrict = districts.length ? Math.max(...districts.map(d => d.count)) : 1;
  const byPlanType = s.breakdown_by_plan_type || {};

  return (
    <Layout title="Itinerary Dashboard">
      <div className="itin-dashboard">
        <div className="itin-page-header">
          <h2>Itinerary Planner Dashboard</h2>
          <button className="itin-refresh-btn" onClick={fetchStats}>↻ Refresh</button>
        </div>

        {/* Summary cards */}
        <div className="itin-stat-grid">
          <div className="itin-stat-card blue">
            <div className="itin-stat-icon">📋</div>
            <div className="itin-stat-body">
              <div className="itin-stat-value">{s.total_generated ?? '—'}</div>
              <div className="itin-stat-label">All-Time Itineraries</div>
            </div>
          </div>
          <div className="itin-stat-card green">
            <div className="itin-stat-icon">📅</div>
            <div className="itin-stat-body">
              <div className="itin-stat-value">{s.total_this_week ?? '—'}</div>
              <div className="itin-stat-label">This Week</div>
            </div>
          </div>
          <div className="itin-stat-card purple">
            <div className="itin-stat-icon">🗓️</div>
            <div className="itin-stat-body">
              <div className="itin-stat-value">{s.total_this_month ?? '—'}</div>
              <div className="itin-stat-label">This Month</div>
            </div>
          </div>
          <div className="itin-stat-card orange">
            <div className="itin-stat-icon">⏱️</div>
            <div className="itin-stat-body">
              <div className="itin-stat-value">
                {s.avg_generation_time_ms != null ? `${(s.avg_generation_time_ms / 1000).toFixed(1)}s` : '—'}
              </div>
              <div className="itin-stat-label">Avg Generation Time</div>
            </div>
          </div>
        </div>

        {/* Type breakdown */}
        <div className="itin-row">
          <div className="itin-card">
            <h3 className="itin-card-title">Plan Type Breakdown</h3>
            <div className="itin-type-cards">
              <div className="itin-type-card ai">
                <span className="itin-type-icon">🤖</span>
                <span className="itin-type-count">{byPlanType.ai_generated ?? '—'}</span>
                <span className="itin-type-label">AI Generated</span>
              </div>
              <div className="itin-type-card preset">
                <span className="itin-type-icon">🗂️</span>
                <span className="itin-type-count">{byPlanType.preset ?? '—'}</span>
                <span className="itin-type-label">Preset</span>
              </div>
            </div>
          </div>

          {/* Interest chart */}
          <div className="itin-card itin-chart-card">
            <h3 className="itin-card-title">Interest Popularity</h3>
            {interests.length === 0 ? (
              <div className="itin-empty">No data</div>
            ) : (
              <div className="itin-bar-chart">
                {interests.map((item) => (
                  <div className="itin-bar-row" key={item.interest}>
                    <div className="itin-bar-label">{item.interest}</div>
                    <div className="itin-bar-track">
                      <div
                        className="itin-bar-fill blue"
                        style={{ width: `${(item.count / maxInterest) * 100}%` }}
                      />
                    </div>
                    <div className="itin-bar-count">{item.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* District chart */}
        <div className="itin-row">
          <div className="itin-card itin-chart-card">
            <h3 className="itin-card-title">Top Districts</h3>
            {districts.length === 0 ? (
              <div className="itin-empty">No data</div>
            ) : (
              <div className="itin-bar-chart">
                {districts.map((item) => (
                  <div className="itin-bar-row" key={item.district}>
                    <div className="itin-bar-label">{item.district}</div>
                    <div className="itin-bar-track">
                      <div
                        className="itin-bar-fill green"
                        style={{ width: `${(item.count / maxDistrict) * 100}%` }}
                      />
                    </div>
                    <div className="itin-bar-count">{item.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Top 20 attractions */}
        <div className="itin-card">
          <h3 className="itin-card-title">Top 20 Attractions in Itineraries</h3>
          {topAttractions.length === 0 ? (
            <div className="itin-empty">No data</div>
          ) : (
            <table className="itin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Attraction</th>
                  <th>Category</th>
                  <th>Appearances</th>
                </tr>
              </thead>
              <tbody>
                {topAttractions.slice(0, 20).map((a, idx) => (
                  <tr key={a.name || idx}>
                    <td className="itin-rank">{idx + 1}</td>
                    <td>{a.name}</td>
                    <td><span className="itin-badge neutral">{a.category || '—'}</span></td>
                    <td><strong>{a.appearances ?? a.count ?? '—'}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ItineraryDashboard;
